import { read, utils, write } from 'xlsx';
import React, { useRef, useState, useEffect, useCallback } from 'react';

import { history } from 'store/store';
import { arrayToSentence } from 'utils/';
import { UPLOAD_ITEM } from 'actions/types';
import {
  StyledTip,
  StyledTips,
  StyledForm,
  StyledLabel,
  StyledButton,
  StyledViewer,
  StyledMessage,
  StyledHeading,
  StyledFileName,
  StyledFileInput,
  StyledContainer,
  StyledSubHeading,
  StyledNameWrapper,
  StyledRowErrors,
  StyledTableWrapper,
  StyledButtonWrapper,
  StyledDownLoadButton,
  StyledDownloadWrapper,
} from 'components/Item/Import/Import.styled';
import { Loading } from 'components/Loaders/';
import { RoundButton } from 'components/Buttons/';
import { INVENTORY_PATH } from 'constants/general';
import { IMPORT_SAMPLE_ROWS } from 'constants/inventory';
import { useMobileView, useHttpStatus } from 'hooks/';
import { ReactComponent as TickIcon } from 'icons/tick.svg';
import { ReactComponent as TestIcon } from 'icons/test.svg';
import { white, darkGrey, secondary } from 'constants/color';
import { ReactComponent as UploadIcon } from 'icons/upload.svg';

function Import({ onSubmit, warehouses = [] }) {
  const inputRef = useRef();
  const isMobileView = useMobileView();
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState('');
  const buttonWidth = isMobileView ? 49 : 20;
  const columns = [
    { id: 'A', name: 'Item Name', required: true },
    { id: 'B', name: 'Warehouse' },
    { id: 'C', name: 'Type' },
    { id: 'D', name: 'For' },
    { id: 'E', name: 'Quantity', required: true },
    { id: 'F', name: 'Quantity Unit', required: true },
    { id: 'G', name: 'Sale Price' },
    { id: 'H', name: 'Lease Price' },
    { id: 'I', name: 'Purchase Price' },
    { id: 'J', name: 'Price Unit', required: true },
    { id: 'K', name: 'Category', required: true },
    { id: 'L', name: 'Warranty Expiry Date' },
    { id: 'M', name: 'Expiry Date' },
    { id: 'N', name: 'Description' },
    { id: 'O', name: 'Status' },
  ];
  const [excelData, setExcelData] = useState();
  const [excelFile, setExcelFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [rowErrors, setRowErrors] = useState([]);
  const { result: isUploaded, response: uploadResult } = useHttpStatus({
    actions: [UPLOAD_ITEM],
  });
  const buttonCaption = excelFile ? 'Change File' : 'Upload File';

  useEffect(
    function () {
      if (!isUploaded || !uploadResult) {
        return;
      }
      const { created = [], uploaded = [], errors = [] } = uploadResult;
      if (errors.length) {
        setRowErrors(errors);
        setMessage(
          `Added ${created.length} items to inventory and ${uploaded.length} to Uploaded. Fix these rows and import them again:`,
        );
      } else {
        history.push(
          uploaded.length ? `${INVENTORY_PATH}#uploaded` : INVENTORY_PATH,
        );
      }
    },
    [isUploaded, uploadResult],
  );

  function onChange(e) {
    clearFile('', true);
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.name.toLowerCase().endsWith('.xlsx')) {
        setFile(selectedFile);
        const reader = new FileReader();
        reader.readAsArrayBuffer(selectedFile);
        reader.onload = function (e) {
          if (e?.target?.result instanceof ArrayBuffer) {
            const excel = e.target.result;
            const workbook = read(excel, { type: 'buffer' });
            const [name] = workbook.SheetNames;
            const workSheet = workbook.Sheets[name];
            const data = utils.sheet_to_json(workSheet, {
              header: 'A',
              defval: '',
            });
            if (data.length > 1) {
              setExcelData(data);
              setExcelFile(excel);
              setIsLoading(false);
            } else {
              clearFile('No data found in the attached spreadsheet');
            }
          }
        };
      } else {
        clearFile('Only .xlsx files can be imported');
      }
    } else {
      clearFile('Please select your file');
    }
  }

  /**
   * @param {string} message
   */
  function clearFile(message, loading = false) {
    setFile(null);
    setExcelFile(null);
    setExcelData(null);
    setMessage(message);
    setRowErrors([]);
    setIsLoading(loading);
  }

  // Columns can be in any order and optional ones left out, but names must match
  const onTestImport = useCallback(() => {
    if (!excelData) {
      return false;
    }
    setRowErrors([]);
    const headers = Object.values(excelData[0])
      .map(function (header) {
        return String(header).trim();
      })
      .filter(Boolean);
    const columnNames = columns.map(function ({ name }) {
      return name;
    });
    const missing = columns
      .filter(function ({ name, required }) {
        return required && !headers.includes(name);
      })
      .map(function ({ name }) {
        return name;
      });
    const unknown = headers.filter(function (header) {
      return !columnNames.includes(header);
    });
    if (missing.length) {
      setMessage(
        arrayToSentence({ array: missing, prefix: 'Missing columns: ' }),
      );
      return false;
    }
    if (unknown.length) {
      setMessage(
        arrayToSentence({
          array: unknown,
          prefix: 'Unknown columns (check the spelling): ',
        }),
      );
      return false;
    }
    setMessage('Spreadsheet ready for import');
    return true;
  }, [excelData]);

  function onImportData() {
    if (onTestImport()) {
      const formData = new FormData();
      formData.append('inventory', file);
      onSubmit(formData);
    }
  }

  function onDownLoadTempLate() {
    const workBook = utils.book_new();
    const columnNames = columns.map(function ({ name }) {
      return name;
    });
    // Show the Warehouse column in use when the company's warehouses are loaded
    const [firstWarehouse] = warehouses;
    const sampleRows = IMPORT_SAMPLE_ROWS.map(function (sample, index) {
      const row =
        index === 0 && firstWarehouse
          ? { ...sample, Warehouse: firstWarehouse.name }
          : sample;
      return columnNames.map(function (name) {
        return row[name] ?? '';
      });
    });
    const workSheet = utils.aoa_to_sheet([columnNames, ...sampleRows]);
    utils.book_append_sheet(workBook, workSheet, 'Sheet1');
    const workBookData = write(workBook, { bookType: 'xlsx', type: 'binary' });
    const blob = new Blob([stringToBuffer(workBookData)], {
      type: 'application/octet-stream',
    });
    const link = window.document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'inventory_upload_template.xlsx';
    document.body.appendChild(link); // Required for Firefox
    link.click();
    window.document.body.removeChild(link);

    /**
     * @param {string} string
     */
    function stringToBuffer(string) {
      const buffer = new ArrayBuffer(string.length);
      const view = new Uint8Array(buffer);
      for (let i = 0; i < string.length; i++) {
        view[i] = string.charCodeAt(i) & 0xff;
      }
      return buffer;
    }
  }

  function onFileButtonClick() {
    inputRef.current.click();
  }

  return (
    <StyledContainer>
      <StyledHeading>Import</StyledHeading>
      <StyledSubHeading>
        Select an Excel (.xlsx) file to import
      </StyledSubHeading>
      <StyledTips>Tips:</StyledTips>
      <StyledTip>
        1. Use the column names shown below. Columns marked * are required; the
        rest can be left out or left blank.
      </StyledTip>
      <StyledTip>
        2. Rows with a Warehouse go straight into that warehouse. Rows without
        one wait under Uploaded. Type is durable or perishable, For is sale or
        lease, and dates are YYYY-MM-DD.
      </StyledTip>
      <StyledTip>
        3. After importing the file, you can first “Test Import” to verify the
        data.
      </StyledTip>
      <StyledTip>
        4. Once you verify that the data has been captured correctly, you can
        proceed to click on the “Import” button. This step is final and cannot
        be undone.
      </StyledTip>
      {excelData ? null : (
        <StyledTableWrapper>
          <table>
            <thead>
              <tr>
                <th></th>
                {columns.map(function ({ id }) {
                  return <th key={id}>{id}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                {columns.map(function ({ id, name, required }) {
                  return <td key={id}>{required ? `${name} *` : name}</td>;
                })}
              </tr>
              {Array.from({ length: 5 }, function (_, index) {
                const value = index + 2;
                return (
                  <tr key={value}>
                    <td>{value}</td>
                    {columns.map(function (_, columnIndex) {
                      return <td key={columnIndex}></td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </StyledTableWrapper>
      )}
      <StyledDownloadWrapper>
        <StyledTip>
          You can download the Inventory Import Template here. It has 3 sample
          rows to replace with your own.
        </StyledTip>
        <StyledDownLoadButton onClick={onDownLoadTempLate}>
          Download File
        </StyledDownLoadButton>
      </StyledDownloadWrapper>
      {isLoading ? <Loading /> : null}
      <StyledForm>
        <StyledNameWrapper>
          <StyledButton type="button" onClick={onFileButtonClick}>
            <UploadIcon />
            <StyledLabel htmlFor="file">{buttonCaption}</StyledLabel>
            <StyledFileInput
              id="file"
              type="file"
              ref={inputRef}
              onChange={onChange}
            />
          </StyledButton>
          {file ? (
            <StyledFileName aria-live="polite">{file.name}</StyledFileName>
          ) : null}
        </StyledNameWrapper>
        {message ? (
          <StyledMessage aria-live="polite">{message}</StyledMessage>
        ) : null}
        {rowErrors.length ? (
          <StyledRowErrors>
            {rowErrors.map(function ({ row, column, value, error }, index) {
              const cell = column
                ? `${column}${value ? ` "${value}"` : ''}: `
                : '';
              return <li key={index}>{`Row ${row}: ${cell}${error}`}</li>;
            })}
          </StyledRowErrors>
        ) : null}
        <StyledButtonWrapper>
          <RoundButton
            isDisabled={!file}
            icon={<TestIcon />}
            backColor={secondary}
            caption="Test Import"
            onClick={onTestImport}
            flexWidth={buttonWidth}
          />
          <RoundButton
            caption="Import"
            backColor={white}
            isDisabled={!file}
            icon={<TickIcon />}
            foreColor={darkGrey}
            borderColor={darkGrey}
            onClick={onImportData}
            flexWidth={buttonWidth}
          />
        </StyledButtonWrapper>
      </StyledForm>
      <StyledViewer>
        {excelData ? (
          <StyledTableWrapper $import>
            <table>
              <thead>
                <tr>
                  {Object.keys(excelData[0]).map(function (key) {
                    return <th key={key}>{key}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {excelData.map(function (row, index) {
                  return (
                    <tr key={index}>
                      {Object.keys(row).map(function (key) {
                        return <td key={key}>{row[key]}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </StyledTableWrapper>
        ) : null}
      </StyledViewer>
    </StyledContainer>
  );
}

export default Import;
